import { useEffect, useState } from "react";
import Error from "../components/Error";
import Card from "../components/Card";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { SkeletonGrid } from "../components/Skeleton";
import { useSaved } from "../context/SavedContext";
import { fetchEvent } from "../api";

function Saved() {
  const { saved, clear } = useSaved();
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const [confirmingClear, setConfirmingClear] = useState(false);

  useEffect(() => {
    const ids = Object.keys(saved);
    if (ids.length === 0) {
      setItems([]);
      setIsLoaded(true);
      return undefined;
    }

    const ac = new AbortController();
    setIsLoaded(false);
    setError(null);

    Promise.allSettled(ids.map((id) => fetchEvent(id, ac.signal))).then(
      (results) => {
        if (ac.signal.aborted) return;
        const fulfilled = results
          .filter((r) => r.status === "fulfilled" && r.value?.record)
          .map((r) => r.value.record);
        setItems(fulfilled);
        setIsLoaded(true);
      }
    );

    return () => ac.abort();
  }, [saved]);

  const confirmClear = () => {
    clear();
    setConfirmingClear(false);
  };

  const count = items.length;

  return (
    <div className="container">
      <PageHeader
        eyebrow="Votre sélection"
        title="Mes favoris"
        intro={
          count > 0
            ? `${count} événement${count > 1 ? "s" : ""} mis de côté. Votre sélection reste sur cet appareil.`
            : "Les événements que vous enregistrez restent disponibles à votre retour, sur cet appareil."
        }
      />

      {error && <Error err={error} />}

      {!isLoaded && !error && <SkeletonGrid count={3} />}

      {isLoaded && !error && count === 0 && (
        <EmptyState
          icon="bi-bookmark"
          title="Rien d'enregistré pour l'instant"
          description="Parcourez l'agenda et touchez le signet sur une carte pour retrouver l'événement ici."
          ctaLabel="Découvrir l'agenda"
          ctaTo="/search"
        />
      )}

      {!error && isLoaded && count > 0 && (
        <>
          <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 pa-grid">
            {items.map(
              (item) =>
                item && (
                  <div className="col" key={item.id}>
                    <Card content={item} />
                  </div>
                )
            )}
          </div>

          <div className="pa-saved__foot">
            {confirmingClear ? (
              <div
                role="alertdialog"
                aria-label="Confirmation de suppression"
                className="pa-panel pa-panel--soft pa-saved__confirm"
              >
                <p className="mb-0 pa-small">
                  Vider toute la sélection ? Cette action est irréversible.
                </p>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="pa-btn pa-btn--quiet pa-btn--sm"
                    onClick={() => setConfirmingClear(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="pa-btn pa-btn--danger pa-btn--sm"
                    onClick={confirmClear}
                  >
                    Vider la sélection
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="pa-btn pa-btn--danger"
                onClick={() => setConfirmingClear(true)}
              >
                <i className="bi bi-trash3" aria-hidden="true"></i>
                Vider la sélection
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Saved;
